/**
 * <s-header-scroll> — Wraps the sticky site header (display: contents) and sets
 * `data-hidden` on itself while the visitor scrolls down on small screens, so the
 * header can slide fully out of view. Scrolling up clears it again.
 *
 * Also exposes the announcement strip's height as `--announce-h` (from the child
 * marked `data-announce-strip`) so the header can slide that strip off-screen
 * without shifting page layout.
 */
class SHeaderScroll extends HTMLElement {
  #lastY = 0;
  #ticking = false;
  #mq = window.matchMedia('(max-width: 47.99rem)');
  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  };
  #resizeObserver = new ResizeObserver(() => this.#measure());

  connectedCallback() {
    this.#lastY = window.scrollY;
    const strip = this.querySelector('[data-announce-strip]');
    if (strip) this.#resizeObserver.observe(strip);
    this.#measure();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver.disconnect();
  }

  #measure() {
    const strip = this.querySelector('[data-announce-strip]');
    if (strip) this.style.setProperty('--announce-h', `${strip.offsetHeight}px`);
  }

  #update() {
    const y = Math.max(0, window.scrollY);
    const delta = y - this.#lastY;
    if (!this.#mq.matches || y < 80) {
      this.removeAttribute('data-hidden');
      this.#lastY = y;
      return;
    }
    if (Math.abs(delta) < 8) return;
    // Keep the full header visible while keyboard focus is inside it.
    if (delta > 0 && !this.contains(document.activeElement)) {
      this.setAttribute('data-hidden', '');
    } else if (delta < 0) {
      this.removeAttribute('data-hidden');
    }
    this.#lastY = y;
  }
}

if (!customElements.get('s-header-scroll')) customElements.define('s-header-scroll', SHeaderScroll);
